import type { ReactNode } from "react";

import type { CatalogListItem } from "@/lib/catalog-item";
import { cn } from "@/lib/utils";
import { ItemCard } from "./item-card";
import { ItemVariantPreview, type CatalogVariantGroups } from "./item-variant-preview";
import type { ItemActionsProps } from "./item-actions-menu";
import type { ItemSelectionProps } from "./item-select-checkbox";
import type { ItemSearchCard } from "./item-search-card-face";

// The card view: the list's default, and the one the old stack opened on.
//
// **Four columns at every width, and that is the donor's rule rather than a
// simplification of it.** `items-card-list.tsx:80` passes `cols={4} spacing={2}`
// into the shared `ListView`; `GridLayout.toRowCol` turns a plain number into
// `repeat(4, 1fr)` (`grid-layout.tsx:8-22`) and emits it through `sx`
// (`:75-89`). Nothing in that path is responsive — no `minmax()`, no
// `auto-fill`, no MUI `Grid item` props — and the responsive-object form of
// `cols` that `GridLayout` supports has no call site anywhere in the donor. So
// the count does not step down, at any viewport.
//
// This file used to read `grid-cols-1 sm:grid-cols-2 lg:grid-cols-3
// xl:grid-cols-4`, described as "the parity grid". It was not: the ramp was
// written without reading the donor's breakpoints, and because this app's
// breakpoint scale is MUI's by owner ruling (#254 D16 = c, `index.css`), its
// top step sat at 1700px — so a 1440px desktop got three columns against the
// Spec Tool's four (#528). Fixing it at 1440px alone would have left the same
// divergence at every other width; the donor has one rule, so this has one
// class.
//
// The tracks are spelled `[repeat(4,1fr)]`, not `grid-cols-4`, and the
// brackets are the point. Tailwind's `grid-cols-4` expands to `repeat(4,
// minmax(0, 1fr))`; the donor emits a bare `repeat(4, 1fr)`, and a bare `1fr`
// is `minmax(auto, 1fr)` — each track floors at its content's min-content
// width. So the donor's grid grows wider than the results column at narrow
// widths and the column's `overflowX: 'hidden'`
// (`items-list-page.tsx:553-559`, mirrored on `ResultsLayout`) cuts it off:
// whole cards, clipped, rather than four squashed ones.
//
// This spelling was tried once before the card was right and backed out,
// because the truncation the card then carried made its min-content 265px —
// fatter than the 257.5px a 1078px column gives four tracks, so the floored
// grid overflowed and clipped the fourth card at 1440px, the width #528
// measured four whole cards at. Removing that truncation (the donor wraps;
// `item-fields.tsx`, `item-heading.tsx`) dropped the card's min-content to
// 162px, and with it the two spellings are identical from 1200px up — 197.5px
// tracks at 1200, 257.5px at 1440, 377.5px at 1920 — and differ only below
// about 1150px, which is exactly where the donor's floor is the behaviour
// worth having.
//
// The arrangement is still a component rather than a `className` on the page:
// the list view and the table need the same page to hold them, and each owns
// its own arrangement.
//
// A grid of cards is still a *list* of items, so it carries list semantics. A
// screen reader announcing "list, 20 items" is the only way that count reaches
// someone not looking at the grid.

/** A track count the ramp can name — the island's widest step is three. */
type GridTrackCount = 1 | 2 | 3 | 4;

/**
 * A column count per breakpoint, MUI's `{ xs, sm, md, lg, xl }` shape on this
 * app's MUI scale (#254 D16, `index.css`). A step left out inherits the one
 * below it, as an MUI responsive value does.
 */
export interface ItemGridColumns {
  xs: GridTrackCount;
  sm?: GridTrackCount;
  md?: GridTrackCount;
  lg?: GridTrackCount;
  xl?: GridTrackCount;
}

// Literal class names, one per step and count, because Tailwind only emits a
// class it can find spelled out in the source. Bare `repeat(n,1fr)` for the
// reason this file's header gives — it is the island donor's spelling too
// (`ItemSearchIsland/components/Layout.tsx:97-103`).
const TRACKS: Record<keyof ItemGridColumns, Record<GridTrackCount, string>> = {
  xs: {
    1: "grid-cols-[repeat(1,1fr)]",
    2: "grid-cols-[repeat(2,1fr)]",
    3: "grid-cols-[repeat(3,1fr)]",
    4: "grid-cols-[repeat(4,1fr)]",
  },
  sm: {
    1: "sm:grid-cols-[repeat(1,1fr)]",
    2: "sm:grid-cols-[repeat(2,1fr)]",
    3: "sm:grid-cols-[repeat(3,1fr)]",
    4: "sm:grid-cols-[repeat(4,1fr)]",
  },
  md: {
    1: "md:grid-cols-[repeat(1,1fr)]",
    2: "md:grid-cols-[repeat(2,1fr)]",
    3: "md:grid-cols-[repeat(3,1fr)]",
    4: "md:grid-cols-[repeat(4,1fr)]",
  },
  lg: {
    1: "lg:grid-cols-[repeat(1,1fr)]",
    2: "lg:grid-cols-[repeat(2,1fr)]",
    3: "lg:grid-cols-[repeat(3,1fr)]",
    4: "lg:grid-cols-[repeat(4,1fr)]",
  },
  xl: {
    1: "xl:grid-cols-[repeat(1,1fr)]",
    2: "xl:grid-cols-[repeat(2,1fr)]",
    3: "xl:grid-cols-[repeat(3,1fr)]",
    4: "xl:grid-cols-[repeat(4,1fr)]",
  },
};

function trackClasses(columns: ItemGridColumns): string {
  return (Object.keys(TRACKS) as (keyof ItemGridColumns)[])
    .map((step) => {
      const count = columns[step];
      return count === undefined ? null : TRACKS[step][count];
    })
    .filter(Boolean)
    .join(" ");
}

export function ItemCardGrid({
  items,
  variantGroups,
  columns,
  searchCardFor,
  leading,
  trailing,
  selection,
  actions,
}: {
  items: readonly CatalogListItem[];
  /** The page's variant groups, which each card looks its own strip up in. */
  variantGroups?: CatalogVariantGroups;
  /**
   * A responsive column ramp — the Item Search island's (#798, breakdown
   * ruling 3). Absent, the grid is the catalog donor's four columns at every
   * width, and its class list is exactly what it was before the prop existed.
   */
  columns?: ItemGridColumns;
  /**
   * The Item Search island's bundle for each card, asked of the card's own item
   * (#798) — how the host marks the pinned card selected and gates the rest.
   * Present, every card draws the island's face and its own 28px strip; the
   * variant preview stays this grid's, one per card. Absent, the cards are the
   * catalog's.
   */
  searchCardFor?: (item: CatalogListItem) => ItemSearchCard;
  /**
   * The Item Search island's own grid items before and after the cards, as
   * `<li>`s — the pinned card's skeleton, the new-query skeletons and the
   * paging sentinel, which the donor draws as cells of the same grid
   * (`ItemSearchIsland.tsx:212-280`) (#811). Absent on the catalog page.
   */
  leading?: ReactNode;
  trailing?: ReactNode;
} & ItemSelectionProps &
  ItemActionsProps) {
  return (
    <ul
      aria-label="Catalog items"
      className={
        columns ? cn("grid gap-4", trackClasses(columns)) : "grid grid-cols-[repeat(4,1fr)] gap-4"
      }
    >
      {leading}
      {items.map((item) => (
        <li key={item.id} className="h-full">
          <ItemVariantPreview item={item} variantGroups={variantGroups}>
            {({ item: displayItem, tiles, state }) =>
              searchCardFor ? (
                <ItemCard
                  item={displayItem}
                  ownItem={item}
                  search={searchCardFor(item)}
                  variantPreview={state}
                />
              ) : (
                <ItemCard
                  item={displayItem}
                  ownItem={item}
                  variantTiles={tiles}
                  selection={selection}
                  actions={actions}
                />
              )
            }
          </ItemVariantPreview>
        </li>
      ))}
      {trailing}
    </ul>
  );
}
